import React, { useRef, useState } from "react";
import axios from "axios";

interface UploadResponse {
  success: boolean;
  url: string;
  filename: string;
}

interface FileUploaderProps {
  onUploadComplete?: (uploadedUrl: string) => void;
  accept?: string;
  maxSizeMB?: number;
  folder?: string;
}

const DEFAULT_MAX_MB = 10;

export const FileUploaderWithProgress: React.FC<FileUploaderProps> = ({
  onUploadComplete,
  accept = "application/pdf",
  maxSizeMB = DEFAULT_MAX_MB,
  folder = "files",
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [uploadedUrl, setUploadedUrl] = useState<string>("");

  const MAX_SIZE = maxSizeMB * 1024 * 1024;

  const validateFile = (f?: File) => {
    if (!f) return;

    if (f.size > MAX_SIZE) {
      alert(`Max ${maxSizeMB}MB allowed`);
      return;
    }

    setFile(f);
  };

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    validateFile(e.target.files?.[0]);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    validateFile(e.dataTransfer.files?.[0]);
  };

  const uploadFile = async () => {
    if (!file) return alert("Select a file first");

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("folder", folder);

      setProgress(0);

      const res = await axios.post<UploadResponse>(
        "/api/admin/adminuploadfile",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`, // adjust if needed
          },
          onUploadProgress: (event) => {
            if (!event.total) return;
            const percent = Math.round(
              (event.loaded * 100) / event.total
            );
            setProgress(percent);
          },
        }
      );

      setUploadedUrl(res.data.url);
      onUploadComplete?.(res.data.url);
    } catch (err: any) {
      alert(err?.response?.data?.message || "Upload failed");
      setProgress(0);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto mt-6">
      {/* Drop zone */}
      <div
        className="border-2 border-dashed border-gray-400 rounded-lg p-6 text-center cursor-pointer hover:bg-gray-50"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept={accept}
          ref={inputRef}
          className="hidden"
          onChange={handleSelect}
        />

        {!file ? (
          <p className="text-gray-500">Click or Drag & Drop a file</p>
        ) : (
          <div className="text-sm text-gray-700">
            <p className="font-medium">{file.name}</p>
            <p className="text-xs text-gray-500">
              {(file.size / 1024 / 1024).toFixed(2)} MB
            </p>
          </div>
        )}
      </div>

      {/* Upload button */}
      <button
        onClick={uploadFile}
        disabled={!file}
        className="w-full bg-blue-600 text-white mt-4 py-2 rounded
                   hover:bg-blue-700 disabled:bg-gray-400"
      >
        Upload
      </button>

      {/* Progress bar */}
      {progress > 0 && (
        <div className="w-full bg-gray-200 h-2 rounded mt-3">
          <div
            className="bg-blue-600 h-2 rounded transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {/* Uploaded result */}
      {uploadedUrl && (
        <div className="mt-4 bg-green-100 p-3 rounded text-sm break-all">
          <p className="font-medium text-green-700">Uploaded</p>
          <a
            href={uploadedUrl}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 underline"
          >
            {uploadedUrl}
          </a>
        </div>
      )}
    </div>
  );
};